import { Growl } from 'angular/custom/services/growl.service';
import {
    AfterViewInit,
    Directive,
    ElementRef,
    EventEmitter,
    Output
} from '@angular/core';
import { ErrorHandlerService } from 'angular/custom/services/error-handler.service';
import { FormControl, NgControl } from '@angular/forms';

export type ProgressiveInputCallbackAndValue = {
    cb: (boolean) => void;
    value: string;
};

@Directive({
    selector: '[pkpProgressiveInput]'
})
export class ProgressiveInput implements AfterViewInit {
    @Output('pkpProgressiveInput') onSaveEventEmitter =
        new EventEmitter<ProgressiveInputCallbackAndValue>();
    elem: JQuery;
    private tagNameLowerCase: string;
    private lastSavedViewValue: string;
    private control: FormControl;

    constructor(
        private growl: Growl,
        private errorHandlerService: ErrorHandlerService,
        private el: ElementRef<HTMLInputElement | HTMLTextAreaElement>,
        control: NgControl
    ) {
        this.control = control.control as FormControl;
    }

    inputViewChangeListener() {
        if (this.control && this.control.value) {
            if (this.tagNameLowerCase === 'input') {
                this.elem.attr(
                    'size',
                    1 + this.control.value.toString().length
                );
            }
        }
    }

    ngAfterViewInit(): void {
        this.control.registerOnChange(this.inputViewChangeListener.bind(this));
        this.elem = $(this.el.nativeElement);

        /**
         * Sometimes (observed this especially in Chrome), Ctrl+Enter causes the blur event on the element leading to double
         * form submission, thus we need to take care of that state.
         */
        let currentlySubmittingViaCtrlEnter: boolean;
        let hasAlreadyHadFocusOnce: boolean = false;
        let clickOnUndoCausingBlur: boolean;
        let clickOnCheckCausingBlur: boolean;
        let textAreaHasAutoGrowHandlerAttached: boolean = false;

        this.tagNameLowerCase = this.el.nativeElement.tagName.toLowerCase();

        const progressiveInput: JQuery = this.elem
            .wrap('<div class="progressive-input"></div>')
            .parent();
        const pencil: JQuery = $(
            '<i class="fa fa-pencil" title="Edit" aria-hidden="true"></i>'
        );
        const check: JQuery = $(
            '<i class="fa fa-check" title="Save (Ctrl+Enter)" aria-hidden="true"></i>'
        );
        const undo: JQuery = $(
            '<i class="fa fa-undo" title="Undo (Esc)" aria-hidden="true"></i>'
        );
        const loading: JQuery = $(
            `<div id="loading-bar-spinner-inline"><div class="spinner-icon-inline"></div></div>`
        );

        this.elem.before(pencil);
        this.elem.before(check);
        this.elem.before(undo);
        this.elem.before(loading);

        if (
            this.tagNameLowerCase === 'input' &&
            !this.elem.parents('.input-group').length
        ) {
            progressiveInput.addClass('inline-block');
        }

        this.elem.on('keyup', () => {
            this.inputViewChangeListener();

            if (this.lastSavedViewValue === this.control.value) {
                check.addClass('disabled');
                undo.addClass('disabled');
            } else {
                check.removeClass('disabled');
                undo.removeClass('disabled');
            }

            if (this.control.invalid) {
                check.addClass('disabled');
            }
        });

        progressiveInput.mouseenter(() => {
            if (!this.elem.is(':focus') && !this.elem.prop('disabled')) {
                pencil.fadeIn();
            }
        });

        progressiveInput.mouseleave(() => {
            pencil.fadeOut();
        });

        pencil.click(() => {
            this.elem.focus();
        });

        this.elem.focus(() => {
            if (!textAreaHasAutoGrowHandlerAttached) {
                // @ts-ignore
                this.elem.autogrow({ vertical: true, horizontal: false });
                textAreaHasAutoGrowHandlerAttached = true;
            }

            if (!hasAlreadyHadFocusOnce) {
                hasAlreadyHadFocusOnce = true;
                this.lastSavedViewValue = this.control.value;
            }

            if (this.lastSavedViewValue === this.control.value) {
                check.addClass('disabled');
                undo.addClass('disabled');
            } else {
                check.removeClass('disabled');
                undo.removeClass('disabled');
            }

            if (this.control.invalid) {
                check.addClass('disabled');
            }

            check.fadeIn();
            undo.fadeIn();
        });

        this.elem.on('blur', () => {
            if (!clickOnUndoCausingBlur && !currentlySubmittingViaCtrlEnter) {
                submit();
                return;
            }

            clickOnUndoCausingBlur = false;
        });

        undo.on('mousedown', () => {
            this.control.setValue(this.lastSavedViewValue);
            this.inputViewChangeListener();

            clickOnUndoCausingBlur = true;
            check.addClass('disabled');
            undo.addClass('disabled');
        });

        check.on('mousedown', () => {
            clickOnCheckCausingBlur = true;
        });

        this.elem.on('keydown', (e: JQueryKeyEventObject) => {
            if (this.control.value === this.lastSavedViewValue) {
                return;
            }

            if (e.ctrlKey && e.keyCode === 13 && this.control.valid) {
                currentlySubmittingViaCtrlEnter = true;
                submit(() => (currentlySubmittingViaCtrlEnter = false));
            } else if (e.keyCode === 27) {
                this.control.setValue(this.lastSavedViewValue);
                this.inputViewChangeListener();
            }
        });

        const submit = (cb?: (cb?: boolean) => void) => {
            const valueToBeSaved: any = this.control.value;

            if (
                this.control.value === this.lastSavedViewValue ||
                this.control.invalid
            ) {
                check.fadeOut(0);
                undo.fadeOut(0);
                cb && cb(false);
                return;
            }

            toggleLoading();

            const responseFn = (success) => {
                if (success) this.lastSavedViewValue = valueToBeSaved;
                toggleLoading();
                check.fadeOut(0);
                undo.fadeOut(0);
                pencil.fadeOut(0);
                cb && cb(true);
            };

            this.onSaveEventEmitter.emit({
                cb: responseFn,
                value: this.control.value
            });
        };

        const toggleLoading: () => void = () => {
            loading.toggle();
            progressiveInput.toggleClass('loading');
            this.elem.prop('disabled', !this.elem.prop('disabled'));
        };
    }
}
